/*!
 * BlockGuard 0.2.0
 * Universal AdBlock & DNS-Blocker Detection - stop nasty tools from interfering with your website's ads and analytics.
 * (c) 2026 Ponk445 - MIT License
 * https://github.com/Ponk445/AD-BlockGuard
 */

// -- Anti-Tamper: Snapshot native methods before any extension can proxy them --
const _native = (() => {
  if (typeof window === 'undefined') return null;
  return {
    // dom
    createElement: Document.prototype.createElement.bind(document),
    appendChild: Node.prototype.appendChild,
    removeChild: Node.prototype.removeChild,
    contains: Node.prototype.contains,
    setAttribute: Element.prototype.setAttribute,
    getComputedStyle: window.getComputedStyle.bind(window),
    getEntriesByType: (window.performance && Performance.prototype.getEntriesByType) ? Performance.prototype.getEntriesByType.bind(window.performance) : null,
    getEntriesByName: (window.performance && Performance.prototype.getEntriesByName) ? Performance.prototype.getEntriesByName.bind(window.performance) : null,
    // timing
    setTimeout: window.setTimeout.bind(window),
    clearTimeout: window.clearTimeout.bind(window),
    // network
    fetch: window.fetch.bind(window),
    Promise: window.Promise,
    MutationObserver: window.MutationObserver ? window.MutationObserver : null
  };
})();

const DEFAULT_TEST_URLS = [
  'https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js',
  'https://securepubads.g.doubleclick.net/tag/js/gpt.js',
  'https://connect.facebook.net/en_US/fbevents.js',
  'https://static.ads-twitter.com/uwt.js',
  'https://cdn.taboola.com/libtrc/loader.js',
  'https://www.googleadservices.com/pagead/conversion.js',
  'https://s.amazon-adsystem.com/iu3',
  'https://pixel.ads.linkedin.com/collect',
];

const DEFAULT_BAIT_CONFIGS = [
  { classes: 'adsbygoogle ad-slot ads-container AdContent', attrs: { 'data-ad-client': 'ca-pub-123' } },
  { classes: 'ad_box ad-placement advertisement-area pub_300x250 BannerAd', attrs: { 'aria-hidden': 'false', 'data-ad-type': 'fixed' } },
  { classes: 'bottom-ad-banner sponsored-link-container text-ads-entry', attrs: { 'id': 'ad-unit-01' } },
];

const generateId = () => Math.random().toString(36).substring(2, 11);



class BlockGuard {
  static version = '0.2.0';
static _isBot() {
  if (typeof window === 'undefined') return false;
  if (window.navigator?.webdriver) return true; // Catches modern headless browsers
  const ua = window.navigator?.userAgent?.toLowerCase() ?? '';
  return ['bot', 'spider', 'crawler', 'googlebot', 'bingbot', 'lighthouse',
  'headlesschrome', 'puppeteer', 'phantomjs'].some(s => ua.includes(s));
}
  constructor(options = {}) {
    // SSR guard (Next.js, Nuxt SSR etc.)
    this._isBrowser = typeof window !== 'undefined' && typeof document !== 'undefined';

    this.config = {
      // Core
      checkOnLoad: options.checkOnLoad !== false,
      debug: options.debug === true,

      // Detection methods
      networkTest: options.networkTest !== false,       // Primary; DNS blockers
      bannerScan: options.bannerScan === true,         // Secondary; visual blockers, DEFAULT OFF

      // Network config
      testUrls: options.testUrls || DEFAULT_TEST_URLS,   // Completely replace defaults
      extraTestUrls: options.extraTestUrls || [],             // Append to defaults
      networkTestCount: options.networkTestCount || 3,
      networkTimeout: options.networkTimeout || 5000,
      networkThreshold: options.networkThreshold || 2,           // Min blocked to trigger

      // Banner scan config (only relevant if bannerScan: true)
      customBaitClasses: options.customBaitClasses || [],
      baitCheckInterval: options.baitCheckInterval || 100,
      baitCheckAttempts: options.baitCheckAttempts || 5,

      // Analytics
      gtag: options.gtag === true,      // Auto-fire gtag if present, DEFAULT OFF (need on in config.)
      gtagEventName: options.gtagEventName || 'adblock_detected',
      gtagParams: options.gtagParams || {},

      // Custom analytics callback (result, reason, details) => void
      analyticsCallback: options.analyticsCallback || null,

      // Result callbacks
      onDetected: options.onDetected || null,
      onNotDetected: options.onNotDetected || null,
      onResult: options.onResult || null,  // Always fired: (detected, reason, details)
    };

    this._state = {
      isChecking: false,
      result: null,
      reason: null,
      details: {},
      baits: [],
      observer: null,
      networkResults: [],
    };

    this._callbacks = { detected: [], notDetected: [] };

    if (this.config.onDetected) this.onDetected(this.config.onDetected);
    if (this.config.onNotDetected) this.onNotDetected(this.config.onNotDetected);

    if (this._isBrowser && this.config.checkOnLoad) {
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.check());
      } else {
        setTimeout(() => this.check(), 1);
      }
    }

    if (typeof Object.freeze === 'function') {
      // Freeze config so can't be accidentally mutated it after init
      Object.freeze(this.config);
    }
  }

  // --- Public API -------------
  onDetected(fn) { if (typeof fn === 'function') this._callbacks.detected.push(fn); return this; }
  onNotDetected(fn) { if (typeof fn === 'function') this._callbacks.notDetected.push(fn); return this; }
  getResult() { return this._state.result; }
  getReason() { return this._state.reason; }
  getDetails() { return this._state.details; }
  getState() {
    return Object.freeze({
      detected: this._state.result,
      reason: this._state.reason,
      checking: this._state.isChecking,
      details: { ...this._state.details },
    });
  }

  async check() {
    if (!this._isBrowser) return null;
    if (BlockGuard._isBot()) { this._log('Bot detected, skipping'); return null; }

    if (this._state.isChecking) { this._log('Check already running'); return null; }

    if (this._state.result !== null) {
      this._fireCallbacks(this._state.result);
      return this._state.result;
    }

    this._state.isChecking = true;
    this._log('Detection started');

    // 1: Banner scan (if enabled)
    if (this.config.bannerScan) {
      this._injectBaits();
      const baitDetected = await this._executeBaitChecks();
      if (baitDetected) return this._state.result;
    }

    // 2: Network test
    if (this.config.networkTest) {
      const networkDetected = await this._runNetworkTests();
      if (networkDetected) {
        this._triggerDetection('network_blocked');
      } else {
        this._triggerClean();
      }
    } else {
      this._triggerClean();
    }

    return this._state.result;
  }

  reset() {
    this._state.isChecking = false;
    this._cleanup();
    this._state.result = null;
    this._state.reason = null;
    this._state.details = {};
    this._state.networkResults = [];
    return this;
  }

  destroy() {
    this._cleanup();
    this._callbacks.detected = [];
    this._callbacks.notDetected = [];
  }

  // ---- Network Tests --------

  async _runNetworkTests() {
    const allUrls = [
      ...this.config.testUrls,
      ...this.config.extraTestUrls,
    ];

    const picked = allUrls
      .sort(() => Math.random() - 0.5)
      .slice(0, this.config.networkTestCount);

    this._log('Network tests:', picked);

    const results = await Promise.all(
      picked.map((url, i) => this._testUrl(url, i))
    );

    this._state.networkResults = results;
    this._state.details.networkResults = results;

    const blockedCount = results.filter(r => !r.reachable).length;
    this._log(`Network: ${blockedCount}/${results.length} blocked`);

    return blockedCount >= this.config.networkThreshold;
  }

  _testUrl(url, index) {
    return new _native.Promise((resolve) => {
      const testUrl = url + (url.includes('?') ? '&' : '?') + '_bg=' + generateId();
      let timer;

      const done = (reachable, reason) => {
        _native.clearTimeout(timer);
        resolve({ url, reachable, reason });
      };

      timer = _native.setTimeout(() => done(false, 'timeout'), this.config.networkTimeout);

      // Use snapshotted fetch to bypass ServiceWorker/Extension hooks
      _native.fetch(testUrl, { method: 'HEAD', mode: 'no-cors', cache: 'no-store' })
        .then(response => {
          _native.setTimeout(() => {
            const entry = this._getPerfEntry(testUrl);

            if (!entry) {
              done(false, 'blocked_no_entry');
              return;
            }

            // Anti-neuter check
            // real scripts are much bigger than those replaced with. if replaced by a blank line, the request gives 200ok, but that is a bad signal
            if (entry.transferSize !== undefined && entry.transferSize > 0 && entry.transferSize < 60) {
              done(false, 'neutered_fake_response');
              return;
            }

            if (entry.duration < 2) {
              done(false, 'neutered_instant_load');
              return;
            }

            done(true, 'reachable');
          }, 100);
        })
        .catch(err => {
          done(false, 'err_blocked_by_client');
        });
    });
  }

_getPerfEntry(url) {
  if (typeof _native.getEntriesByType !== 'function') return null;
  
  // Try exact match first using SNAPSHOTTED method
  if (typeof _native.getEntriesByName === 'function') {
      const entries = _native.getEntriesByName(url);
      if (entries.length > 0) return entries[entries.length - 1];
  }

  // Fallback to partial match
  const base = url.split('?')[0];
  const allResources = _native.getEntriesByType('resource');
  return allResources.reverse().find(e => e.name.includes(base)) || null;
}

  // Banner / Bait Scan

_injectBaits() {
  if (!document.body) {
    setTimeout(() => this._injectBaits(), 50);
    return;
  }

  // Create a single container for all baits to isolate DOM observation
  const baitContainer = _native.createElement.call(document, 'div');
  baitContainer.id = `bg-container-${generateId()}`;
  baitContainer.style.cssText = 'width:1px!important;height:1px!important;position:absolute!important;left:-10000px!important;top:-10000px!important;z-index:-1!important;';
  
  const configs = [
    ...DEFAULT_BAIT_CONFIGS,
    ...this.config.customBaitClasses.map(cls => ({ classes: cls, attrs: {} })),
  ];

  configs.forEach(cfg => {
    const el = _native.createElement.call(document, 'div');
    el.id = `ad-${generateId()}`;
    el.className = cfg.classes;
    Object.entries(cfg.attrs).forEach(([k, v]) => el.setAttribute(k, v));
    el.style.cssText = 'width:1px!important;height:1px!important;';
    el.appendChild(Object.assign(_native.createElement.call(document, 'ins'), { className: 'adsbygoogle' }));
    
    this._state.baits.push(el);
    _native.appendChild.call(baitContainer, el); // add to container
  });

  _native.appendChild.call(document.body, baitContainer);
  this._state.baitContainer = baitContainer; // Save for cleanup

  // reflow
  void baitContainer.offsetHeight;

  if (_native.MutationObserver) {
    this._state.observer = new _native.MutationObserver(mutations => {
      let shouldCheck = false;
      for (const m of mutations) {
        if (m.type === 'childList') {
          for (const node of m.removedNodes) {
            // Check if the removed node is our container OR one of the baits
            if (node === baitContainer || this._state.baits.includes(node)) {
              this._triggerDetection('bait_removed');
              return;
            }
          }
        }
        if (m.type === 'attributes' && (this._state.baits.includes(m.target) || m.target === baitContainer)) {
          shouldCheck = true;
        }
      }
      
      if (shouldCheck) {
         setTimeout(() => {
          if (this._checkBaitsBlocked()) this._triggerDetection('bait_modified');
        }, 50);
      }
    });

    // Observe only the container and the baits (observing body had perf risks (fuller sites))
    this._state.observer.observe(baitContainer, { childList: true, attributes: true, attributeFilter: ['style', 'class', 'hidden'] });
    this._state.baits.forEach(bait => {
      this._state.observer.observe(bait, { attributes: true, attributeFilter: ['style', 'class', 'hidden'] });
    });
  }
}

  _checkBaitsBlocked() {
    for (const el of this._state.baits) {
      if (!document.body.contains(el)) return true;
      // Only flag offsetParent null if the bait IS IN the DOM (not if parent is legit hidden) (check: somethng made element disappear)
      if (el.offsetParent === null && document.body.contains(el)) {
        const s = _native.getComputedStyle(el)
        if (s.display !== 'none') return true; // /(check) hidden becasue parent is hidden, not directly the bait
      }
      if (el.offsetHeight === 0 || el.offsetWidth === 0) return true;
      if (window.getComputedStyle) {
        const s = _native.getComputedStyle(el)
        if (s.display === 'none' || s.visibility === 'hidden' || parseFloat(s.opacity) === 0) return true;
      }
    }
    if (document.body.getAttribute('abp') !== null) return true;
    return false;
  }

  async _executeBaitChecks() {
    return new Promise(resolve => {
      let attempts = 0;
      // check and report
      const check = () => {
        if (this._state.result !== null) { resolve(true); return; }
        if (this._checkBaitsBlocked()) {
          this._triggerDetection('bait_blocked');
          resolve(true);
          return;
        }
        // increment it till reaches the wished attempt count
        if (++attempts >= this.config.baitCheckAttempts) { resolve(false); return; }
        setTimeout(check, this.config.baitCheckInterval);
      };
      check();
      this._log(`Bait check (${this.config.baitCheckAttempts} attempts)`);
    });
  }

  // -- Trigger / Callbacks ---------
  // these are called by our functions, and trigger things

  _triggerDetection(reason) {
    if (!this._state.isChecking || this._state.result !== null) return;
    this._log('DETECTED:', reason);
    this._state.result = true;
    this._state.reason = reason;
    this._finalize(true);
  }

  _triggerClean() {
    if (this._state.result !== null) return;
    this._log('CLEAN');
    this._state.result = false;
    this._state.reason = 'all_passed';
    this._finalize(false);
  }

  _finalize(detected) {
    this._cleanup();

    // built-in gtag
    if (detected && this.config.gtag && typeof window.gtag === 'function') {
      window.gtag('event', this.config.gtagEventName, {
        reason: this._state.reason,
        ...this.config.gtagParams,
      });
      this._log('gtag fired:', this.config.gtagEventName);
    }

    // Custom analytics callback; maybe not so reliable
    if (typeof this.config.analyticsCallback === 'function') {
      try {
        this.config.analyticsCallback(detected, this._state.reason, this._state.details);
      } catch (e) { console.error('[BlockGuard] analyticsCallback error:', e); }
    }

    // onResult (always)
    if (typeof this.config.onResult === 'function') {
      try { this.config.onResult(detected, this._state.reason, this._state.details); }
      catch (e) { console.error('[BlockGuard] onResult error:', e); }
    }

    this._fireCallbacks(detected);
  }

  _fireCallbacks(detected) {
    const list = this._callbacks[detected ? 'detected' : 'notDetected'];
    list.forEach(fn => { try { fn(); } catch (e) { console.error('[BlockGuard] Callback error:', e); } });
  }

// cleans all up, so there is no race cndition, etc.
_cleanup() {
  if (this._state.baitContainer && this._state.baitContainer.parentNode) {
      this._state.baitContainer.parentNode.removeChild(this._state.baitContainer);
  }
  this._state.baitContainer = null;
  this._state.baits = [];
  this._state.observer?.disconnect();
  this._state.observer = null;
  this._state.isChecking = false;
}

  _log(...args) {
    if (this.config.debug) console.log('[BlockGuard]', ...args);
  }
}

export default BlockGuard;
